/**
 * @fileoverview Date range picker component with calendar interface and preset integration.
 * @module shared/components/ui/DateRangePicker
 */

import { ReactElement } from 'react'
import { useFilter } from '@/shared/hooks/useFilter'
import RangePickerPopover from './RangePickerPopover'
import { DateRange } from 'react-day-picker'

/**
 * Props for the DateRangePicker component.
 */
interface DateRangePickerProps {
  /** Additional CSS classes for customization */
  className?: string
  /**
   * The selected date range.
   * If not provided, the component will use the date range from the filter context.
   */
  value?: DateRange | undefined
  /**
   * Callback function that is called when the date range is changed.
   * If not provided, the component will use the `setDateRange` function from the filter context.
   */
  onChange?: (range: DateRange | undefined) => void
}

/**
 * Date range picker component that integrates with FilterContext.
 * Provides a calendar interface for custom date range selection.
 *
 * @component
 * @example
 * ```tsx
 * <DateRangePicker />
 * ```
 */
export function DateRangePicker({
  className = '',
  value: controlledValue,
  onChange: controlledOnChange,
}: DateRangePickerProps): ReactElement {
  const { startDate, endDate, setDateRange } = useFilter()

  const value: DateRange | undefined =
    controlledValue ?? (startDate && endDate ? { from: startDate, to: endDate } : undefined)

  return (
    <RangePickerPopover
      className={className}
      value={{ from: value?.from, to: value?.to }}
      onChange={(from, to) => {
        if (controlledOnChange) {
          controlledOnChange({ from, to })
        } else {
          setDateRange(from ?? null, to ?? null)
        }
      }}
      align="end"
    />
  )
}
